import { DEFAULT_TOKEN_PATH, getDefaultHubOptions } from "./hub/default-options.ts";
import type { MonitorHubOptions } from "./hub/monitor-hub-options.ts";
import {
  GROUP_HUB_ON,
  HUB_INVOKE_LEAVE_GROUP_ROWS,
  HUB_INVOKE_LEAVE_NUMBER_ROWS,
  HUB_INVOKE_NUMBERS_BATCH_ROWS,
  HUB_INVOKE_ROWS,
  NUMBER_HUB_ON,
  ON_USER_NUMBERS_EVENT,
  SIMPLE_HUB_ON,
} from "./hub/registry.ts";

interface LoginToken {
  access_token: string | undefined;
  expires_date?: Date;
  issued_date?: Date;
}

/** Intenta obtener token del BFF (incluye cookies OIDC); un reintento corto si el primer 401 coincidió con renovación de sesión. */
async function fetchLoginTokenFromBff(
  tokenFetchUrl: string,
): Promise<LoginToken | undefined> {
  for (let attempt = 0; attempt < 2; attempt++) {
    const response = await fetch(tokenFetchUrl, {
      credentials: "include",
      cache: "no-store",
    }).catch((err: unknown) => {
      console.warn(err);
      return undefined;
    });
    if (response?.ok) {
      try {
        return (await response.json()) as LoginToken;
      } catch {
        return undefined;
      }
    }
    if (response?.status === 401 && attempt === 0) {
      await new Promise((r) => setTimeout(r, 150));
      continue;
    }
    break;
  }
  return undefined;
}

/** Conexión al hub ASP.NET Core SignalR; los eventos se entregan a `onHubEvent`. */
export class MonitorHubConnection {
  private connection: HubConnection | null = null;
  private loginToken: LoginToken | null = null;
  /** Se resuelve tras el primer `UserNumbers` recibido en la secuencia actual de `configureConnection`. */
  private userNumbersSubscriptionGate: (() => void) | null = null;
  private readonly hubUrl: string;
  private readonly tokenFetchUrl: string;
  private readonly onHubEvent: (
    hubEvent: string,
    user: unknown,
    item: unknown,
  ) => void;

  readonly options: MonitorHubOptions;

  constructor(
    hubUrl: string,
    optionsInput: Partial<MonitorHubOptions> | undefined,
    tokenFetchUrl: string,
    onHubEvent: (hubEvent: string, user: unknown, item: unknown) => void,
  ) {
    this.hubUrl = hubUrl;
    this.tokenFetchUrl = tokenFetchUrl;
    this.onHubEvent = onHubEvent;
    this.options = { ...getDefaultHubOptions(), ...optionsInput };
    void this.buildConnection();
  }

  async configureConnection(): Promise<void> {
    const conn = this.connection;
    if (!conn) return;

    if (this.options.userNumbers) {
      const timeoutMs = this.options.userNumbersWaitTimeoutMs ?? 15_000;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      const waitFirstSnapshot = new Promise<void>((resolve, reject) => {
        timeoutId = setTimeout(() => {
          this.userNumbersSubscriptionGate = null;
          reject(new Error("timeout UserNumbers"));
        }, timeoutMs);
        this.userNumbersSubscriptionGate = () => {
          if (timeoutId !== undefined) clearTimeout(timeoutId);
          resolve();
        };
      });
      try {
        await conn.invoke("UserNumbers");
        await waitFirstSnapshot.catch(() => {
          console.warn(
            "MonitorHub: timeout esperando UserNumbers; continuando con MyJoin e invokes.",
          );
        });
      } catch (err: unknown) {
        this.userNumbersSubscriptionGate = null;
        if (timeoutId !== undefined) clearTimeout(timeoutId);
        console.error(String(err instanceof Error ? err.message : err));
      }
    }
    if (this.options.call_NumbersUsers_MyJoin) {
      try {
        await conn.invoke("Call_NumbersUsers_MyJoin");
      } catch (err: unknown) {
        console.error(String(err instanceof Error ? err.message : err));
      }
    } else {
      if (this.options.call_Numbers_MyJoin) {
        try {
          await conn.invoke("Call_Numbers_MyJoin");
        } catch (err: unknown) {
          console.error(String(err instanceof Error ? err.message : err));
        }
      }
      if (this.options.call_Users_MyJoin) {
        try {
          await conn.invoke("Call_Users_MyJoin");
        } catch (err: unknown) {
          console.error(String(err instanceof Error ? err.message : err));
        }
      }
    }

    for (const [optKey, methodName] of HUB_INVOKE_ROWS) {
      if (!this.options[optKey]) continue;
      void conn.invoke(methodName).catch((err: unknown) => {
        console.error(String(err instanceof Error ? err.message : err));
      });
    }

    for (const [optKey, methodName] of HUB_INVOKE_LEAVE_GROUP_ROWS) {
      if (!this.options[optKey]) continue;
      void conn.invoke(methodName).catch((err: unknown) => {
        console.error(String(err instanceof Error ? err.message : err));
      });
    }

    for (const [optKey, methodName] of HUB_INVOKE_LEAVE_NUMBER_ROWS) {
      if (!this.options[optKey]) continue;
      void conn.invoke(methodName).catch((err: unknown) => {
        console.error(String(err instanceof Error ? err.message : err));
      });
    }

    const batchPayload = this.options.numbersJoinInvokePayload ?? {};
    for (const [optKey, methodName] of HUB_INVOKE_NUMBERS_BATCH_ROWS) {
      if (!this.options[optKey]) continue;
      void conn.invoke(methodName, batchPayload).catch((err: unknown) => {
        console.error(String(err instanceof Error ? err.message : err));
      });
    }
  }

  async stop(): Promise<void> {
    const conn = this.connection;
    if (!conn) return;

    try {
      await conn.stop();
    } catch (err: unknown) {
      console.error(String(err instanceof Error ? err.message : err));
    } finally {
      this.connection = null;
    }
  }

  private async buildConnection(): Promise<void> {
    const sr = getSignalR();
    const me = this;

    this.connection = new sr.HubConnectionBuilder()
      .configureLogging(sr.LogLevel.None)
      .withUrl(this.hubUrl, {
        accessTokenFactory: async () => {
          const currentDate = new Date();
          if (
            !me.loginToken ||
            me.loginToken.expires_date === undefined ||
            me.loginToken.expires_date === null ||
            me.loginToken.expires_date < currentDate
          ) {
            const json = await fetchLoginTokenFromBff(me.tokenFetchUrl);
            if (!json?.access_token) {
              return "";
            }
            me.loginToken = json;
            if (me.loginToken.expires_date) {
              me.loginToken.expires_date = new Date(me.loginToken.expires_date);
            }
            if (me.loginToken.issued_date) {
              me.loginToken.issued_date = new Date(me.loginToken.issued_date);
            }
          }
          return me.loginToken?.access_token ?? "";
        },
      })
      .withAutomaticReconnect()
      .build();

    this.registerOns();
    this.connection.onreconnected(() => {
      void this.configureConnection().catch((err: unknown) => {
        console.error(String(err instanceof Error ? err.message : err));
      });
    });
    try {
      await this.connection.start();
      await this.configureConnection();
    } catch (err: unknown) {
      console.error(String(err instanceof Error ? err.message : err));
    }
  }

  private registerOns(): void {
    const conn = this.connection;
    if (!conn) return;

    for (const [optKey, eventName] of SIMPLE_HUB_ON) {
      if (!this.options[optKey]) continue;
      conn.on(eventName, (user, item) => {
        this.onHubEvent(eventName, user, item);
      });
    }
    for (const [optKey, eventName] of GROUP_HUB_ON) {
      if (!this.options[optKey]) continue;
      conn.on(eventName, (user, item) => {
        this.onHubEvent(eventName, user, item);
      });
    }
    for (const [optKey, eventName] of NUMBER_HUB_ON) {
      if (!shouldRegisterNumberHubOn(this.options, optKey, eventName)) continue;
      conn.on(eventName, (user, item) => {
        this.onHubEvent(eventName, user, item);
      });
    }

    if (this.options.userNumbers) {
      conn.on(ON_USER_NUMBERS_EVENT, (user: unknown, item: unknown) => {
        this.onHubEvent(ON_USER_NUMBERS_EVENT, user, item);
        const gate = this.userNumbersSubscriptionGate;
        if (gate) {
          this.userNumbersSubscriptionGate = null;
          gate();
        }
      });
    }
  }
}

export { DEFAULT_TOKEN_PATH };

function isUserNumberHubEvent(eventName: string): boolean {
  return eventName.startsWith("User") && eventName.endsWith("_Number");
}

function shouldRegisterNumberHubOn(
  options: MonitorHubOptions,
  optKey: string,
  eventName: string,
): boolean {
  if (options[optKey] || options.call_Numbers_AllJoin) return true;
  if (isUserNumberHubEvent(eventName)) {
    return !!(options.call_Users_MyJoin || options.call_NumbersUsers_MyJoin);
  }
  return !!(options.call_Numbers_MyJoin || options.call_NumbersUsers_MyJoin);
}

function getSignalR(): typeof signalR {
  const g = globalThis as unknown as { signalR?: typeof signalR };
  if (typeof g.signalR !== "undefined" && g.signalR) return g.signalR;
  throw new Error(
    "Debe cargarse signalr.min.js (Microsoft) antes que aircall-monitor.js.",
  );
}
